import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, LogOut, Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { manualeFor } from "@/components/Header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { GlobalSearch } from "@/components/shell/GlobalSearch";
import { BellMenu } from "@/components/shell/BellMenu";
import { AreeMenu } from "@/components/shell/AreeMenu";
import { MobileMenu } from "@/components/shell/MobileMenu";

const UserMenu = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" aria-label="Profilo" className="rounded-full p-0.5 hover:ring-2 hover:ring-sage transition-shadow">
          <Avatar className="h-9 w-9 border border-border">
            {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
            <AvatarFallback className="bg-sage text-navy text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="font-medium truncate text-navy">{user?.name}</span>
          <span className="text-xs text-slate-500 font-normal truncate">{user?.email}</span>
          <span className="text-[10px] uppercase tracking-widest text-[#0B7770] mt-1">{user?.role === "admin" ? "Amministratore" : "Utente"}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-archivio-novita" onClick={() => nav("/novita")} className="cursor-pointer"><Sparkles className="h-4 w-4 mr-2" /> Archivio novità</DropdownMenuItem>
        {user?.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="cursor-pointer"><ShieldCheck className="h-4 w-4 mr-2" /> Pannello Admin</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/login"); }} className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"><LogOut className="h-4 w-4 mr-2" /> Esci</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TopBar = () => {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const man = manualeFor(pathname);
  const openMan = () => (man.route ? nav(man.route) : window.open(`/manuali/${man.file}.pdf`, "_blank"));
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-border" data-testid="app-header">
      <div className="h-16 flex items-center gap-3 px-4 md:px-8">
        <MobileMenu />
        <button onClick={() => nav("/dashboard")} data-testid="header-logo-btn" className="shrink-0" aria-label="Vai alla Home">
          <img src={LOGO_URL} alt="Assietica Suite" className="h-11 w-auto object-contain" data-testid="header-logo" />
        </button>
        <GlobalSearch className="hidden lg:block flex-1 max-w-xl mx-auto" />
        <div className="ml-auto lg:ml-0 flex items-center gap-1">
          {man && (
            <button onClick={openMan} data-testid="nav-manuale-btn" title="Manuale operatore del modulo" className="h-10 px-3 rounded-lg flex items-center gap-2 text-sm text-navy hover:bg-sage transition-colors">
              <BookOpen className="h-4 w-4" /> <span className="hidden lg:inline">Manuale</span>
            </button>
          )}
          <BellMenu />
          <UserMenu />
        </div>
      </div>
      <div className="lg:hidden h-12 px-4 md:px-8 pb-2 flex items-center"><GlobalSearch className="w-full max-w-xl" /></div>
      <AreeMenu />
    </header>
  );
};
